import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/modules/growing-circle/lib/api";
import AudioPlayer from "@/modules/growing-circle/components/AudioPlayer";
import TestimonyCard from "@/modules/growing-circle/components/TestimonyCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ContentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [testimonies, setTestimonies] = useState([]);
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const [visibility, setVisibility] = useState("community");
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get(`/contents/${id}`).then(({ data }) => setContent(data)).catch(() => {});
    api.get(`/testimonies`, { params: { content_id: id } }).then(({ data }) => setTestimonies(data)).catch(() => {});
  };
  useEffect(load, [id]);

  const share = async () => {
    if (!body.trim()) return toast.error("Write something before sharing.");
    setSending(true);
    try {
      await api.post("/testimonies", { title, body, visibility, content_id: id });
      setBody(""); setTitle("");
      toast.success("Thank you for sharing.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (!content) return <p className="text-muted-foreground">One moment…</p>;

  return (
    <div className="max-w-3xl">
      <button onClick={() => navigate("/growing-circle/podcasts")} className="text-sm text-muted-foreground flex items-center gap-1 mb-8 hover:text-foreground transition-colors">
        <ArrowLeft className="w-4 h-4" /> All listens
      </button>

      <span className="text-xs uppercase tracking-widest text-accent">{content.type}{content.chapter ? ` · ${content.chapter}` : ""}</span>
      <h1 className="font-heading text-4xl tracking-tight mt-2 mb-6">{content.title}</h1>
      {content.audio_url && <div className="mb-8"><AudioPlayer src={content.audio_url} /></div>}
      {content.description && <p className="text-muted-foreground leading-relaxed mb-4">{content.description}</p>}
      {content.body && <p className="text-foreground leading-relaxed whitespace-pre-wrap mb-10">{content.body}</p>}

      <div className="bg-secondary/50 border border-border rounded-3xl p-8 mb-12">
        <h2 className="font-heading text-2xl mb-2">What resonates with you?</h2>
        <p className="text-muted-foreground text-sm mb-6">If you wish, share a part of yourself. No one will judge you.</p>
        <div className="space-y-4">
          <Input placeholder="A title (optional)" className="rounded-xl bg-background" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="content-testimony-title" />
          <Textarea placeholder="Write here…" className="rounded-xl bg-background min-h-32" value={body} onChange={(e) => setBody(e.target.value)} data-testid="content-testimony-body" />
          <RadioGroup value={visibility} onValueChange={setVisibility} className="flex flex-wrap gap-3">
            {[["community", "Visible to the community"], ["anonymous", "Anonymous to the community"], ["listeners", "Only to those who listen"]].map(([v, l]) => (
              <label key={v} className="flex items-center gap-2 text-sm border border-border rounded-full px-4 py-2 cursor-pointer bg-background">
                <RadioGroupItem value={v} /> {l}
              </label>
            ))}
          </RadioGroup>
          <Button className="btn-gold rounded-full text-sm font-medium" disabled={sending} onClick={share} data-testid="content-share-btn">
            {sending ? "Sending…" : "Share"}
          </Button>
        </div>
      </div>

      {testimonies.length > 0 && (
        <div>
          <h2 className="font-heading text-2xl tracking-tight mb-6">Those who shared here</h2>
          <div className="space-y-4">
            {testimonies.map((t) => <TestimonyCard key={t.id} t={t} onChange={load} />)}
          </div>
        </div>
      )}
    </div>
  );
}
